import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/data/db';
import { deleteSessionCascade } from '@/data/repositories/sessions';
import { exportJsonBundle, exportScoresCsv, exportTrialsCsv, exportLearningCsv } from '@/data/export';
import { dotProbeProtocolSchema } from '@/domain/core/protocol.schema';
import { deleteProtocol, listProtocols, registerProtocol, seedBuiltinProtocols } from '@/data/repositories/protocols';
import { evaluate, useApp } from '@/state/store';
import { Button, Panel, VerdictPill } from '@/components/ui';

export function Researcher() {
  const go = useApp((s) => s.go);
  const calibration = useApp((s) => s.calibration);
  const sessions = useLiveQuery(() => db.sessions.orderBy('startedAt').reverse().toArray(), []) ?? [];
  const counts = useLiveQuery(async () => ({
    participants: await db.participants.count(), sessions: await db.sessions.count(),
    trials: await db.trials.count(), scores: await db.scores.count(), events: await db.events.count(),
    interpretation: await db.learningSessions.count(),
  }), []);
  const [msg, setMsg] = useState<string | null>(null);
  const protocols = useLiveQuery(() => listProtocols(), []) ?? [];
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [protoText, setProtoText] = useState('');
  const [protoResult, setProtoResult] = useState<{ ok: true; id: string } | { ok: false; issues: string[] } | null>(null);

  const run = (label: string, fn: () => Promise<void>) => fn().then(() => setMsg(`${label} 완료`)).catch((e: unknown) => setMsg(`${label} 실패: ${String(e)}`));

  const validateProtocol = () => {
    try {
      const parsed = dotProbeProtocolSchema.safeParse(JSON.parse(protoText));
      if (parsed.success) setProtoResult({ ok: true, id: `${parsed.data.id}@${parsed.data.version}` });
      else setProtoResult({ ok: false, issues: parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`) });
    } catch (e) { setProtoResult({ ok: false, issues: [`JSON 파싱 실패: ${String(e)}`] }); }
  };

  return (
    <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-6 px-6 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-accent-lit)]">CBMI · 연구자 패널</span>
          <h1 className="text-2xl font-semibold text-[var(--color-screen-fg)]">데이터 · 내보내기 · 진단</h1>
        </div>
        <Button variant="ghost" onClick={() => go('home')}>홈</Button>
      </header>

      <Panel title="저장소">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 font-mono text-[12.5px] sm:grid-cols-5">
          {counts ? (Object.entries(counts) as [string, number][]).map(([k, v]) => (
            <div key={k} className="flex flex-col gap-0.5"><dt className="text-[10px] uppercase tracking-[0.1em] text-[var(--color-screen-dim)]">{k}</dt><dd className="text-[var(--color-screen-fg)]">{v}</dd></div>
          )) : null}
        </dl>
      </Panel>

      <Panel title="내보내기">
        <div className="flex flex-col gap-3">
          <p className="text-[13.5px] text-[#5D7066]">CSV 는 롱 포맷, 매 행에 <span className="font-mono">sign_convention</span> 포함. JSON 번들에는 시드·프로토콜 해시·기기 스탬프가 들어 있어 세션을 완전 재현할 수 있다.</p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => void run('trials_long.csv', exportTrialsCsv)}>trials_long.csv</Button>
            <Button onClick={() => void run('scores.csv', exportScoresCsv)}>scores.csv</Button>
            <Button onClick={() => void run('해석·역할극 CSV', exportLearningCsv)}>해석·역할극 CSV</Button>
            <Button variant="ghost" onClick={() => void run('JSON 번들', exportJsonBundle)}>JSON 번들</Button>
          </div>
          <p className="text-xs text-[var(--color-screen-dim)]">해석·역할극 CSV는 장면별 첫 응답·단어 입력 시도·응답시간을 포함합니다. 전체 JSON에는 제시한 시나리오와 선택지 순서, 버전, 시드가 함께 보존됩니다. 역할극의 target_matched는 열린 해석 선택 여부이며 임상 점수가 아닙니다.</p>
          {msg ? <p className="font-mono text-[12px] text-[var(--color-screen-dim)]">{msg}</p> : null}
        </div>
      </Panel>

      <Panel title="세션">
        {sessions.length === 0 ? <p className="text-[13px] text-[var(--color-screen-dim)]">없음</p> : (
          <ul className="flex flex-col font-mono text-[12px]">
            {sessions.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-[#E5EBE3] py-2 first:border-t-0 first:pt-0">
                <span className="text-[var(--color-screen-fg)]">{s.participantCode} · {s.arm} · {s.supervision} · seed {s.rngSeed}</span>
                <span className="flex items-center gap-3 text-[var(--color-screen-dim)]">
                  {new Date(s.startedAt).toLocaleString('ko-KR')}{s.abortedReason ? ` · ${s.abortedReason}` : ''}
                  <button type="button" className="text-[var(--color-warn)] hover:underline" onClick={() => { if (confirm(`세션 ${s.id.slice(0, 8)} 와 트라이얼·점수를 삭제할까요?`)) void run('삭제', () => deleteSessionCascade(s.id)); }}>삭제</button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="등록된 프로토콜">
        <ul className="flex flex-col font-mono text-[12px]">
          {protocols.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-[#E5EBE3] py-2 first:border-t-0 first:pt-0">
              <span className="text-[var(--color-screen-fg)]">{p.key} <span className="text-[var(--color-screen-dim)]">· {p.name} · {p.source} · {p.hash.slice(0, 8)}</span></span>
              {p.source === 'imported' ? <button type="button" className="text-[var(--color-warn)] hover:underline" onClick={() => { if (confirm(`${p.key} 삭제? 이미 실행된 세션은 해시로 원본을 참조하므로 영향 없음.`)) void run('프로토콜 삭제', () => deleteProtocol(p.id)); }}>삭제</button> : null}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="프로토콜 가져오기 (JSON → 스키마 검증 → 등록)">
        <div className="flex flex-col gap-3">
          <textarea value={protoText} onChange={(e) => setProtoText(e.target.value)} rows={6} placeholder='{"id": "...", "task": "CBM_A", ...}'
            className="w-full rounded border border-[#D9E3DB] bg-[var(--color-screen)] p-3 font-mono text-[12px] text-[var(--color-screen-fg)]" />
          <div className="flex gap-3">
            <Button variant="ghost" onClick={validateProtocol} disabled={!protoText.trim()}>검증만</Button>
            <Button disabled={!protoText.trim()} onClick={() => void run('프로토콜 등록', async () => { const rec = await registerProtocol(JSON.parse(protoText), 'imported'); setProtoResult({ ok: true, id: rec.key }); })}>검증 후 등록</Button>
          </div>
          {protoResult ? protoResult.ok
            ? <p className="font-mono text-[12px] text-[var(--color-accent-lit)]">통과 · {protoResult.id}</p>
            : <ul className="font-mono text-[12px] text-[var(--color-warn)]">{protoResult.issues.map((i) => <li key={i}>{i}</li>)}</ul> : null}
          <p className="text-[12px] text-[var(--color-screen-dim)]">훈련 우연성 &lt; 0.95 는 여기서 거부된다. 같은 id@version 이 이미 있으면 덮어쓰지 않는다 — 버전을 올려야 한다. 등록된 프로토콜은 세션 설정에서 선택한다.</p>
        </div>
      </Panel>

      <Panel title="캘리브레이션 진단">
        {calibration ? (
          <ul className="flex flex-col gap-1 font-mono text-[12px]">
            {evaluate(calibration).map((c) => <li key={c.id} className="flex justify-between gap-4"><span className="text-[#5D7066]">{c.label}</span><span className="flex items-center gap-3"><span className="text-[var(--color-screen-dim)]">{c.value}</span><VerdictPill v={c.verdict} /></span></li>)}
          </ul>
        ) : <p className="text-[13px] text-[var(--color-screen-dim)]">이 세션에서 캘리브레이션을 실행하지 않았습니다.</p>}
      </Panel>

      <Panel title="위험 구역">
        <div className="flex flex-wrap items-center gap-3">
          {!confirmWipe ? <Button variant="ghost" onClick={() => setConfirmWipe(true)}>모든 데이터 삭제…</Button> : (
            <>
              <span className="text-[13px] text-[var(--color-warn)]">되돌릴 수 없습니다. 먼저 내보내기를 하셨습니까?</span>
              <Button onClick={() => { setConfirmWipe(false); void run('전체 삭제', async () => { await db.delete(); await db.open(); await seedBuiltinProtocols(); }); }}>예, 전부 삭제</Button>
              <Button variant="ghost" onClick={() => setConfirmWipe(false)}>취소</Button>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
